import { useState, useEffect, useCallback } from 'react';
import { getPersonServices } from '../services/person-services.service';

const toOkState = (items) => ({ items, status: 'ok' });
const toErrorState = () => ({ items: [], status: 'error' });
const toLoadingState = () => ({ items: [], status: 'loading' });

const applyRowsToServicesMap = (prev, rows) => {
  const next = { ...prev };
  rows.forEach(({ id, items, ok }) => {
    next[id] = ok ? toOkState(items) : toErrorState();
  });
  return next;
};

const fetchServicesForPerson = async (personId) => {
  try {
    const items = await getPersonServices(personId);
    return { id: personId, items: Array.isArray(items) ? items : [], ok: true };
  } catch {
    return { id: personId, items: [], ok: false };
  }
};

/**
 * Prefetch person service assignments for each row shown in results.
 * Returns `{ servicesByPersonId, refreshPersonServices }`.
 */
export function usePersonResultServices(results) {
  const [servicesByPersonId, setServicesByPersonId] = useState({});

  useEffect(() => {
    if (!results.length) {
      setServicesByPersonId({});
      return undefined;
    }

    const personIds = results.map((row) => row.personId);
    setServicesByPersonId((prev) => {
      const next = { ...prev };
      personIds.forEach((id) => {
        next[id] = toLoadingState();
      });
      return next;
    });

    let cancelled = false;
    (async () => {
      const rows = await Promise.all(results.map((person) => fetchServicesForPerson(person.personId)));
      if (cancelled) {
        return;
      }

      setServicesByPersonId((prev) => applyRowsToServicesMap(prev, rows));
    })();

    return () => {
      cancelled = true;
    };
  }, [results]);

  const refreshPersonServices = useCallback(async (personId) => {
    const row = await fetchServicesForPerson(personId);
    setServicesByPersonId((prev) => ({
      ...prev,
      [personId]: row.ok ? toOkState(row.items) : toErrorState(),
    }));
  }, []);

  return { refreshPersonServices, servicesByPersonId };
}
